/**
 * recordingPackage (Welle 7e / pre96) — baut aus den aufgezeichneten Spuren das
 * Export-Paket: Medien + Manifest + Dialogprotokoll + SHA-256-Prüfsummen, als ZIP.
 *
 * Vollständig client-seitig, dependency-frei (eigener Store-ZIP-Writer). MP3-16k-Transcode
 * und GPG-Verschlüsselung sind bewusst in eine spätere (serverseitige) Pre-Release verschoben;
 * pre96 verpackt die Browser-nativen Spuren (webm/opus) und referenziert sie schema-korrekt.
 */

import { EXPORT_PROFILES, type ExportProfileId } from './recordingProfiles';
import { jsPDF } from 'jspdf';

export type PartyName = 'berater' | 'besucher' | 'kombiniert';

export interface RecordedTrack {
  party: PartyName;
  blob: Blob;
  /** Dateiendung der Rohaufnahme, z. B. 'webm' */
  ext: string;
}

export type ConsentMode = 'hinweis' | 'einwilligung';

export interface RecordingMeta {
  callId: string;
  topic: string;
  mifidRelevant: boolean;
  startedAt: number;   // epoch ms
  endedAt: number;     // epoch ms
  agents: string[];
  guests: string[];
  consentMode: ConsentMode;
  consentAt?: number;  // epoch ms — Zeitpunkt der protokollierten Einwilligung/Belehrung
  // konfigurierbare Geschäfts-/Kundenmetadaten (MiFID-Bezug)
  kundenname?: string;
  personenNummer?: string;
  ordernummer?: string;
  geburtsdatum?: string;
  telefonnummer?: string;
  filiale?: string;
  /** Aufbewahrungsfrist in Jahren (Default 5; behördlich auf 7 verlängerbar) */
  retentionYears?: number;
  /** Geräte-/Verbindungskontext fürs Protokoll (optional) */
  device?: { os?: string; browser?: string; ip?: string };
}

export interface BuiltPackage {
  blob: Blob;
  fileName: string;
  profile: ExportProfileId;
  /** Prüfsummen je enthaltener Datei (für Audit/Quittung) */
  checksums: Record<string, string>;
  rootHash: string;
}

// ── SHA-256 (hex) ───────────────────────────────────────────────────────────
async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function esc(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
function iso(ms?: number): string { return ms ? new Date(ms).toISOString() : ''; }

// ── CRC32 + Store-ZIP (dependency-frei) ──────────────────────────────────────
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

interface ZipEntry { name: string; data: Uint8Array; }

function dosDateTime(d: Date): { time: number; date: number } {
  const time = ((d.getHours() & 0x1f) << 11) | ((d.getMinutes() & 0x3f) << 5) | ((Math.floor(d.getSeconds() / 2)) & 0x1f);
  const date = (((d.getFullYear() - 1980) & 0x7f) << 9) | (((d.getMonth() + 1) & 0x0f) << 5) | (d.getDate() & 0x1f);
  return { time, date };
}

function zipStore(entries: ZipEntry[]): Uint8Array {
  const enc = new TextEncoder();
  const { time, date } = dosDateTime(new Date());
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const e of entries) {
    const nameBytes = enc.encode(e.name);
    const crc = crc32(e.data);
    const size = e.data.length;

    const lh = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(lh.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);            // version needed
    lv.setUint16(6, 0x0800, true);        // flags: UTF-8 name
    lv.setUint16(8, 0, true);             // method: store
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);         // comp size
    lv.setUint32(22, size, true);         // uncomp size
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);            // extra len
    lh.set(nameBytes, 30);
    locals.push(lh, e.data);

    const ch = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(ch.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);            // version made by
    cv.setUint16(6, 20, true);            // version needed
    cv.setUint16(8, 0x0800, true);        // flags
    cv.setUint16(10, 0, true);            // method
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);            // extra len
    cv.setUint16(32, 0, true);            // comment len
    cv.setUint16(34, 0, true);            // disk start
    cv.setUint16(36, 0, true);            // internal attrs
    cv.setUint32(38, 0, true);            // external attrs
    cv.setUint32(42, offset, true);       // local header offset
    ch.set(nameBytes, 46);
    centrals.push(ch);

    offset += lh.length + e.data.length;
  }

  const cdSize = centrals.reduce((n, c) => n + c.length, 0);
  const cdOffset = offset;
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, cdOffset, true);

  const parts = [...locals, ...centrals, eocd];
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let p = 0;
  for (const part of parts) { out.set(part, p); p += part.length; }
  return out;
}

// ── Manifeste / Protokoll ─────────────────────────────────────────────────────
function buildCanonicalManifest(meta: RecordingMeta, files: { name: string; party: PartyName; sha256: string; bytes: number }[], rootHash: string) {
  const years = meta.retentionYears ?? 5;
  const willBeDeletedAt = meta.endedAt + years * 365.25 * 24 * 3600 * 1000;
  return JSON.stringify({
    schemaVersion: '1.0',
    producer: 'bamoa-videochat',
    callId: meta.callId,
    topic: meta.topic,
    mifidRelevant: meta.mifidRelevant,
    recordedContent: 'audio',
    recordingStartedAt: iso(meta.startedAt),
    recordingEndedAt: iso(meta.endedAt),
    retention: { years, willBeDeletedAt: iso(willBeDeletedAt), extended: false, deletedAt: null },
    consent: { mode: meta.consentMode, at: iso(meta.consentAt) },
    participants: { agents: meta.agents, guests: meta.guests },
    business: {
      kundenname: meta.kundenname ?? null, personenNummer: meta.personenNummer ?? null,
      ordernummer: meta.ordernummer ?? null, geburtsdatum: meta.geburtsdatum ?? null,
      telefonnummer: meta.telefonnummer ?? null, filiale: meta.filiale ?? null,
    },
    files: files.map((f) => ({ recordedFor: 'audio', party: f.party, fileName: f.name, checksum: f.sha256, algo: 'SHA-256', bytes: f.bytes, isOk: true })),
    integrity: { rootHash, algo: 'SHA-256' },
  }, null, 2);
}

function buildCompetitorInfoXml(meta: RecordingMeta, files: { name: string; party: PartyName; sha256: string; format: string }[]) {
  const years = meta.retentionYears ?? 5;
  const willBeDeletedAt = meta.endedAt + years * 365.25 * 24 * 3600 * 1000;
  const results = files.map((f) =>
    `<result><recordedFor>audio</recordedFor><format>${esc(f.format)}</format>` +
    `<fileName>${esc(f.name)}</fileName><amountOrigins>1</amountOrigins>` +
    `<checksum>${esc(f.sha256)}</checksum><isOk>true</isOk></result>`).join('');
  const guestFields =
    `<field order="1" name="guestPersonNumberField" label="Personen-Nr.">${esc(meta.personenNummer ?? '')}</field>` +
    `<field order="4" name="guestNameField" label="Kundenname">${esc(meta.kundenname ?? '')}</field>` +
    `<field order="5" name="guestBirthdayField" label="Geburtsdatum">${esc(meta.geburtsdatum ?? '')}</field>` +
    `<field order="7" name="guestOrderNumberField" label="Ordernummer">${esc(meta.ordernummer ?? '')}</field>` +
    `<field order="8" name="guestphoneNumberField" label="Telefonnummer">${esc(meta.telefonnummer ?? '')}</field>` +
    `<field order="6" name="guestNameFromLoginField" label="Besuchername aus der Anmeldung">${esc(meta.filiale ?? '')}</field>`;
  return `<?xml version="1.0" encoding="UTF-8"?>\n<recordingInfo>` +
    `<recordingStartedAt>${esc(iso(meta.startedAt))}</recordingStartedAt>` +
    `<recordingEndedAt>${esc(iso(meta.endedAt))}</recordingEndedAt>` +
    `<willBeDeletedAt>${esc(iso(willBeDeletedAt))}</willBeDeletedAt>` +
    `<extendStorageDuration/><deletedAt/>` +
    `<recordedContent>audio</recordedContent>` +
    `<guestRecordingData>${guestFields}</guestRecordingData>` +
    `<agents>${meta.agents.map((a) => `<agent>${esc(a)}</agent>`).join('')}</agents>` +
    `<guests>${meta.guests.map((g) => `<guest>${esc(g)}</guest>`).join('')}</guests>` +
    `<results>${results}</results>` +
    `<wasExecutionSuccessful>true</wasExecutionSuccessful>` +
    `</recordingInfo>\n`;
}

function buildProtocol(meta: RecordingMeta, files: { name: string; sha256: string }[]): string[] {
  const L: string[] = [];
  L.push('Dialogprotokoll — Bamoa Videochat Aufzeichnung');
  L.push('');
  L.push(`Call-ID:        ${meta.callId}`);
  L.push(`Thema:          ${meta.topic}  (MiFID-relevant: ${meta.mifidRelevant ? 'ja' : 'nein'})`);
  L.push(`Berater:        ${meta.agents.join(', ') || '—'}`);
  L.push(`Besucher:       ${meta.guests.join(', ') || '—'}`);
  if (meta.device) L.push(`Gerät/Browser:  ${[meta.device.os, meta.device.browser, meta.device.ip].filter(Boolean).join(' · ')}`);
  L.push('');
  L.push('Verlauf:');
  if (meta.consentAt) {
    const verb = meta.consentMode === 'hinweis' ? 'wurde über die Aufzeichnung informiert' : 'hat der Aufzeichnung zugestimmt';
    L.push(`  ${iso(meta.consentAt)}  Besucher ${verb} (${meta.consentMode}).`);
  }
  L.push(`  ${iso(meta.startedAt)}  Aufzeichnung gestartet.`);
  L.push(`  ${iso(meta.endedAt)}  Aufzeichnung beendet.`);
  L.push('');
  L.push('Dateien (SHA-256):');
  for (const f of files) L.push(`  ${f.name}  ${f.sha256}`);
  L.push('');
  L.push('Aufbewahrung: ' + (meta.retentionYears ?? 5) + ' Jahre (gesetzlich; behördlich auf bis zu 7 Jahre verlängerbar).');
  return L;
}

/** Dialogprotokoll als PDF rendern (erste Zeile = Titel). Dependency: jsPDF. */
function renderProtocolPdf(lines: string[]): Uint8Array {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 48;
  const lineH = 14;
  const pageH = doc.internal.pageSize.getHeight();
  const maxW = doc.internal.pageSize.getWidth() - margin * 2;
  let y = margin + 6;

  // Titel
  doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
  doc.text(lines[0] ?? 'Dialogprotokoll', margin, y);
  y += lineH * 1.8;

  // Körper (monospaced für die Hash-Zeilen, ansonsten normal)
  doc.setFontSize(9.5);
  for (let i = 1; i < lines.length; i++) {
    const raw = lines[i];
    const mono = /^\s{2}\S/.test(raw) && raw.includes('  ') && /[0-9a-f]{16,}/.test(raw);
    doc.setFont(mono ? 'courier' : 'helvetica', 'normal');
    const wrapped = doc.splitTextToSize(raw === '' ? ' ' : raw, maxW) as string[];
    for (const w of wrapped) {
      if (y > pageH - margin) { doc.addPage(); y = margin + 6; }
      doc.text(w, margin, y);
      y += lineH;
    }
  }
  const ab = doc.output('arraybuffer') as ArrayBuffer;
  return new Uint8Array(ab);
}

// ── Hauptfunktion ─────────────────────────────────────────────────────────────
export async function buildPackage(profileId: ExportProfileId, tracks: RecordedTrack[], meta: RecordingMeta): Promise<BuiltPackage> {
  const profile = EXPORT_PROFILES[profileId];
  const enc = new TextEncoder();
  const entries: ZipEntry[] = [];
  const checksums: Record<string, string> = {};
  const fileInfos: { name: string; party: PartyName; sha256: string; bytes: number; format: string }[] = [];

  // Medien-Spuren + Prüfsummen
  for (const t of tracks) {
    const name = `${t.party}.${t.ext}`;
    const buf = await t.blob.arrayBuffer();
    const bytes = new Uint8Array(buf);
    const sha = await sha256Hex(buf);
    entries.push({ name, data: bytes });
    checksums[name] = sha;
    fileInfos.push({ name, party: t.party, sha256: sha, bytes: bytes.length, format: t.ext });
  }

  // rootHash = SHA-256 über die konkatenierten Datei-Hashes (stabil, ordnungsabhängig)
  const rootHash = await sha256Hex(enc.encode(fileInfos.map((f) => f.name + ':' + f.sha256).join('|')).buffer as ArrayBuffer);

  // Manifest (profilabhängig)
  if (profile.manifest === 'competitor-info-xml') {
    const xml = buildCompetitorInfoXml(meta, fileInfos);
    entries.push({ name: 'info.xml', data: enc.encode(xml) });
    checksums['info.xml'] = await sha256Hex(enc.encode(xml).buffer as ArrayBuffer);
  } else {
    const json = buildCanonicalManifest(meta, fileInfos, rootHash);
    entries.push({ name: 'manifest.json', data: enc.encode(json) });
    checksums['manifest.json'] = await sha256Hex(enc.encode(json).buffer as ArrayBuffer);
  }

  // Dialogprotokoll (Text; PDF folgt)
  if (profile.attachProtocol) {
    const lines = buildProtocol(meta, fileInfos.map((f) => ({ name: f.name, sha256: f.sha256 })));
    const pdf = renderProtocolPdf(lines);
    entries.push({ name: 'dialogprotokoll.pdf', data: pdf });
    checksums['dialogprotokoll.pdf'] = await sha256Hex(pdf.slice().buffer);
  }

  const zip = zipStore(entries);
  const d = new Date(meta.startedAt);
  const stamp = `${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}_${String(d.getDate()).padStart(2, '0')}` +
    `_${String(d.getHours()).padStart(2, '0')}-${String(d.getMinutes()).padStart(2, '0')}-${String(d.getSeconds()).padStart(2, '0')}`;
  const fileName = `${stamp}_${meta.callId}_Aufzeichnung.zip`;
  // Uint8Array → eigenständigen ArrayBuffer für den Blob übergeben (typsicher)
  const blob = new Blob([zip.slice().buffer], { type: 'application/zip' });
  return { blob, fileName, profile: profileId, checksums, rootHash };
}
